import { BoxOfferCard } from "@modules/boxOffer/components/boxOfferCard"
import { BoxOfferRuleCard } from "@modules/boxOffer/components/boxOfferRuleCard"
import { BoxOfferInvitationStatus, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxOfferInvitationInput } from "@modules/boxOffer/gateway/boxOfferDto"
import { useBoxOffer, useBoxOfferInvitations } from "@modules/boxOffer/hooks/useBoxOffer"
import { getOfferCommitment, getOfferVisibility } from "@modules/boxOffer/utils/boxOfferUtils"
import { useBoxProgrammations } from "@modules/boxProgrammation/hooks/useBoxProgrammations"
import { convertCouponInputToRequest, CouponInputFormik } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import {
  getAmountOfDiscount,
  getPriceAfterDicount,
  getTextDurationType,
} from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUButton } from "@modules/ui/components/huButton"
import { HUTag } from "@modules/ui/components/huTag"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { isInArray } from "@modules/utils/isInArray"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { CalendarDays, ChevronRight, Percent } from "lucide-react"
import { Dispatch, SetStateAction, useMemo } from "react"
import { useTheme } from "styled-components"

export type MemberSendOfferModalStepTwoProps = {
  boxOfferId: string
  userId: string
  handleStep: () => void
  onHide: () => void
  discountInput: CouponInputFormik
  deferredDate: Date | null
  setIsDiscountVisible: Dispatch<SetStateAction<boolean>>
  setIsDeferredDateVisible: Dispatch<SetStateAction<boolean>>
}

export const MemberSendOfferModalStepTwo: React.FC<MemberSendOfferModalStepTwoProps> = ({
  boxOfferId,
  userId,
  handleStep,
  onHide,
  setIsDiscountVisible,
  setIsDeferredDateVisible,
  discountInput,
  deferredDate,
}) => {
  const theme = useTheme()
  const language = useLanguage()
  const toast = useToast()
  const { data: boxOffer } = useBoxOffer(boxOfferId)
  const { result: programs } = useBoxProgrammations({ size: 50 })
  const { sendBoxOfferInvitation, isSendBoxOfferInvitation, data: boxOfferInvitations } = useBoxOfferInvitations(userId)

  const offerCommitment = useMemo(() => {
    return getOfferCommitment(boxOffer)
  }, [boxOffer])

  const visibilitySubtitle = useMemo(() => {
    return getOfferVisibility(boxOffer, theme)
  }, [boxOffer])

  const filteredPrograms = useMemo(
    () => programs.filter((program) => boxOffer?.programs.includes(program.id)),
    [boxOffer?.programs, programs],
  )

  const handleSendOffer = () => {
    if (!boxOffer) return

    if (
      boxOfferInvitations.findIndex(
        (invitation) => invitation.boxOfferId === boxOffer.id && invitation.status === BoxOfferInvitationStatus.PENDING,
      ) >= 0
    ) {
      return toast.show({
        summary: word("member.sendOffer.modal.toast.duplicateError"),
        severity: "error",
      })
    }

    const payload = {
      offerId: boxOffer.id,
      userId,
      coupon: discountInput.value > 0 ? convertCouponInputToRequest(discountInput) : undefined,
      deferredDate: deferredDate ? dayjs(deferredDate).add(1, "day").toISOString().split("T")[0] : undefined,
    } as BoxOfferInvitationInput

    sendBoxOfferInvitation(payload)
      .then(() => {
        toast.show({
          summary: word("member.sendOffer.modal.toast.success", { name: boxOffer.name }),
          severity: "success",
        })
        onHide()
      })
      .catch(() => {
        toast.show({
          summary: word("member.sendOffer.modal.toast.error", { name: boxOffer.name }),
          severity: "error",
        })
      })
  }

  return (
    <div className="flex flex-column w-full">
      {boxOffer && (
        <div className="flex flex-column mt-4 w-full gap-4">
          {/* HEADER */}
          <BoxOfferCard
            offer={boxOffer}
            key={boxOffer.id}
            price={
              boxOffer.price !== null && discountInput.value > 0
                ? {
                    value: getPriceAfterDicount(discountInput, boxOffer.price.value),
                    currency: boxOffer.price.currency,
                  }
                : undefined
            }
            className={`flex w-full h-9rem p-3 gap-4 border-round-xl border-1 justify-content-between border-primary-700 bg-neutral-900`}
          />

          <div className="flex align-items-center justify-content-between w-full px-1">
            <HUText fontStyle="BM" color={theme.neutralColor700}>
              {capitalize(offerCommitment)}
            </HUText>
            <HUText
              fontStyle="BS"
              color={theme.neutralColor700}
              className="flex my-1 gap-1 justify-content-center align-items-center"
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            >
              {visibilitySubtitle.icon}
              {visibilitySubtitle.text}
            </HUText>
          </div>

          {/* DISCOUNT */}
          {boxOffer.price !== null && discountInput.value > 0 && (
            <div className="flex flex-column gap-2">
              <div className="flex gap-2 align-items-center">
                <HUText fontStyle="BS" color={theme.neutralColor700}>
                  {word("box.offer.send.modal.discount.title")}
                </HUText>
                <div className="flex flex-grow-1 border-top-1 border-neutralColor-300"></div>
              </div>
              <div className="flex gap-1 align-items-baseline">
                {discountInput.type === CouponType.PERCENT && (
                  <HUText fontStyle="LL" color={theme.mainWhite}>
                    -{discountInput.value}% =
                  </HUText>
                )}
                <HUText fontStyle="LL" color={theme.mainWhite}>
                  -
                  {formatCurrency(
                    getAmountOfDiscount(discountInput, boxOffer.price.value),
                    boxOffer.price.currency,
                    language,
                  )}
                </HUText>
                {boxOffer.type === BoxOfferType.Subscription && (
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {`(${getTextDurationType(discountInput)})`}
                  </HUText>
                )}
              </div>
            </div>
          )}

          {/* DEFERRED DATE */}
          {isInArray([BoxOfferType.Subscription, BoxOfferType.Millenium], boxOffer.type) && (
            <div className="flex flex-column gap-2">
              <div className="flex gap-2 align-items-center">
                <HUText fontStyle="BS" color={theme.neutralColor700}>
                  {word("box.offer.send.modal.startDate.title")}
                </HUText>
                <div className="flex flex-grow-1 border-top-1 border-neutralColor-300"></div>
              </div>
              <div className="flex gap-1 align-items-baseline">
                <HUText fontStyle="LL" color={theme.mainWhite}>
                  {deferredDate ? dayjs(deferredDate).format("L") : word("box.offer.send.modal.startDate.now")}
                </HUText>
                {deferredDate !== null && (
                  <HUText fontStyle="LM" color={theme.neutralColor400}>
                    {word("box.offer.send.modal.startDate.inDays", {
                      count: dayjs(deferredDate).diff(dayjs().startOf("day"), "day"),
                    })}
                  </HUText>
                )}
              </div>
            </div>
          )}

          {/* NUMBER OF SESSION AND RENEWAL */}
          {((boxOffer.count && boxOffer.count > 0) || boxOffer.renewable) && (
            <div className="flex justify-space-between gap-3 w-full">
              {boxOffer.count && boxOffer.count > 0 && (
                <div className="flex flex-column gap-2 w-6">
                  <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
                    {word("offer.form.numberOfSessions.title").toUpperCase()}
                  </HUText>
                  <BoxOfferRuleCard className="flex align-items-center h-full border-1 border-solid border-neutralColor-200 rounded-card px-5 py-2">
                    <HUText className="w-full text-center justify-content-center	" fontStyle="TS">
                      {word("offer.form.numberOfSessions.included", { count: boxOffer.count ?? 0 })}
                    </HUText>
                  </BoxOfferRuleCard>
                </div>
              )}
              {boxOffer.renewable && (
                <div className="flex flex-column gap-2 w-6">
                  <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
                    {word("box.offer.form.infos.renewal.title").toUpperCase()}
                  </HUText>
                  <BoxOfferRuleCard className="flex align-items-center h-full border-1 border-solid border-neutralColor-200 rounded-card px-5 py-2">
                    <HUText className="w-full text-center" fontStyle="TS">
                      {boxOffer.renewable
                        ? word("box.offer.form.infos.renewal.yes")
                        : word("box.offer.form.infos.renewal.no")}{" "}
                    </HUText>
                  </BoxOfferRuleCard>
                </div>
              )}
            </div>
          )}

          {/* RULES FOR RYTHME */}
          {boxOffer.type !== BoxOfferType.DropIn && boxOffer.type !== BoxOfferType.Trial && (
            <div className="flex flex-column gap-2">
              <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
                {word("box.offer.form.infos.restrictedPeriod.title").toUpperCase()}
              </HUText>
              <div className="flex  gap-2 w-full">
                {boxOffer.rules && boxOffer.rules.length > 0 ? (
                  boxOffer.rules.map((rule, index) => (
                    <BoxOfferRuleCard
                      key={index}
                      className="flex align-items-center h-full border-1 border-solid border-neutralColor-200 rounded-card px-5 py-2"
                    >
                      <HUText className="w-full text-center" fontStyle="TS">{`${rule.value} ${word(
                        "offer.form.rules.sessions",
                        { count: rule.value },
                      )} ${word(`offer.form.rules.period.per.${rule.period}`)}`}</HUText>
                    </BoxOfferRuleCard>
                  ))
                ) : (
                  <BoxOfferRuleCard className="flex align-items-center w-full border-1 border-solid border-neutralColor-200 rounded-card px-5 py-2">
                    <HUText className="w-full text-center" fontStyle="TS">
                      {word("box.offer.form.infos.rules.unlimited")}
                    </HUText>
                  </BoxOfferRuleCard>
                )}
              </div>
            </div>
          )}

          {/* ACTIVITIES */}
          <div className="flex flex-column  gap-2">
            <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
              {word("box.offer.form.infos.activitiesIncluded.title").toUpperCase()}
            </HUText>
            <div
              className="flex flex-column border-1 border-solid border-neutralColor-200 rounded-card px-5 py-4"
              style={{ minHeight: "100px" }}
            >
              <HUText fontStyle="BS">{word("box.offer.resume.activitiesIncluded.details")}</HUText>
              <div className="flex flex-wrap gap-2 mt-2">
                {filteredPrograms.map((program, index) => (
                  <HUTag key={index} className="bg-neutralColor-100">
                    <HUText fontStyle="LM" color={theme.mainWhite}>
                      {program.name}
                    </HUText>
                  </HUTag>
                ))}
              </div>
            </div>
          </div>

          {/* DISCOUNT & START DATE */}
          <div className="flex flex-column gap-1">
            {boxOffer.price && (
              <div
                className="flex align-items-center p-2 gap-2 border-top-1 border-neutralColor-200 cursor-pointer"
                onClick={() => setIsDiscountVisible(true)}
              >
                <Percent color={theme.neutralColor900} strokeWidth={1} size={15} />
                <HUText fontStyle="BM" color={theme.neutralColor900} className="flex flex-grow-1">
                  {discountInput.value
                    ? word("box.offer.send.modal.button.editDiscount")
                    : word("box.offer.send.modal.button.addDiscount")}
                </HUText>
                <ChevronRight className="flex" color={theme.neutralColor900} strokeWidth={1} />
              </div>
            )}

            {isInArray([BoxOfferType.Subscription, BoxOfferType.Millenium], boxOffer.type) && (
              <div
                className="flex align-items-center p-2 gap-2 border-top-1 border-neutralColor-200 cursor-pointer"
                onClick={() => setIsDeferredDateVisible(true)}
              >
                <CalendarDays color={theme.neutralColor900} strokeWidth={1} size={15} />
                <HUText fontStyle="BM" color={theme.neutralColor900} className="flex flex-grow-1">
                  {deferredDate
                    ? word("box.offer.send.modal.button.editStartDate")
                    : word("box.offer.send.modal.button.setStartDate")}
                </HUText>
                <ChevronRight className="flex" color={theme.neutralColor900} strokeWidth={1} />
              </div>
            )}
          </div>

          {/* BUTTON */}
          <div className=" mb-4 mt-3 flex flex-row gap-2 align-items-center justify-content-center">
            <HUButton
              type="Left_Icon"
              size="M"
              colorType="Quaternary"
              text={word("global.return")}
              onClick={handleStep}
              className="w-full"
            />
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Primary"
              text={word("box.offer.send.modal.button.send")}
              onClick={handleSendOffer}
              className="w-full"
              disabled={isSendBoxOfferInvitation}
            />
          </div>
        </div>
      )}
    </div>
  )
}
